import React, { useState } from 'react';
import { Check, X, Users, Shield, UserCheck, Crown, UserCog } from 'lucide-react';
import { toast } from 'react-toastify';

/**
 * Approver Selection Component
 * Allows IT staff to flexibly select which approvers are needed for access tickets
 */
const ApproverSelection = ({ 
  availableApprovers, 
  onApproverSelection, 
  currentApprovers = [],
  allHods = [], // All HODs for multi-selection
  ticket = null, // Ticket object to check approval status
  onResetApprovers = null // Callback for reset functionality
}) => {
  const [selectedApprovers, setSelectedApprovers] = useState(currentApprovers);
  const [selectedHodIds, setSelectedHodIds] = useState(null); // Single HOD selection (not array)
  const [selectionOrder, setSelectionOrder] = useState([]); // Track order of all selections
  const [showResetConfirm, setShowResetConfirm] = useState(false); // Reset confirmation

  const approverTypes = [
    { key: 'manager', label: 'Manager', icon: UserCheck, color: 'blue' },
    { key: 'it_hod', label: 'IT HOD', icon: Users, color: 'purple' },
    { key: 'director', label: 'Managing Director', icon: Crown, color: 'red' }
  ];

  const handleApproverToggle = (approverType) => {
    if (selectedApprovers.includes(approverType)) {
      // Remove from selection
      setSelectedApprovers(selectedApprovers.filter(a => a !== approverType));
      setSelectionOrder(selectionOrder.filter(item => item.type !== 'approver' || item.id !== approverType));
    } else {
      // Add to selection - append to maintain selection order
      setSelectedApprovers([...selectedApprovers, approverType]);
      setSelectionOrder([...selectionOrder, { type: 'approver', id: approverType }]);
    }
  };

  const handleHodToggle = (hodId) => {
    // Single HOD selection - replace previous selection (radio button behavior)
    if (selectedHodIds === hodId) {
      // Clicking same HOD - deselect it
      setSelectedHodIds(null);
      setSelectionOrder(selectionOrder.filter(item => item.type !== 'hod'));
    } else {
      // Selecting new HOD - replace any previous HOD in selectionOrder
      setSelectedHodIds(hodId);
      // Remove any previous HOD selections from order, then add new one
      const orderWithoutHods = selectionOrder.filter(item => item.type !== 'hod');
      setSelectionOrder([...orderWithoutHods, { type: 'hod', id: hodId }]);
    }
  };

  // Check which approvers have already approved (status = 1)
  const getApprovedApprovers = () => {
    const approved = [];
    if (ticket?.ticket_manager === 1) approved.push({ type: 'manager', label: 'Manager' });
    if (ticket?.ticket_hod === 1) approved.push({ type: 'hod', label: 'HOD' });
    if (ticket?.ticket_it_hod === 1) approved.push({ type: 'it_hod', label: 'IT HOD' });
    if (ticket?.ticket_director === 1) approved.push({ type: 'director', label: 'Director' });
    return approved;
  };

  // Check if approvers have been configured (regardless of approval status)
  const hasConfiguredApprovers = () => {
    return currentApprovers && currentApprovers.length > 0;
  };

  const handleSave = () => {
    // Validate at least one approver selected
    if (selectedApprovers.length === 0 && !selectedHodIds) {
      alert('Please select at least one approver');
      return;
    }

    // Return data with selection order preserved
    // Convert single HOD to array format for backend compatibility
    const approversData = {
      approvers: selectedApprovers,
      selectedHodIds: selectedHodIds ? [selectedHodIds] : [],
      selectionOrder: selectionOrder // Array of {type, id} in click order
    };
    onApproverSelection(approversData);
  };

  const handleResetApprovers = () => {
    // Get selected reset options
    const resetApprovers = document.getElementById('reset_approvers_main').checked;
    const resetManager = document.getElementById('reset_manager_approval').checked;
    const resetHod = document.getElementById('reset_hod_approval').checked;
    const resetItHod = document.getElementById('reset_it_hod_approval').checked;
    const resetDirector = document.getElementById('reset_director_approval').checked;

    if (onResetApprovers) {
      // Only pass reset options if at least one is selected
      const resetOptions = {};
      if (resetApprovers) resetOptions.reset_approvers = resetApprovers;
      if (resetManager) resetOptions.reset_manager = resetManager;
      if (resetHod) resetOptions.reset_hod = resetHod;
      if (resetItHod) resetOptions.reset_it_hod = resetItHod;
      if (resetDirector) resetOptions.reset_director = resetDirector;

      // Pass the reset options to the parent component (only if any are selected)
      if (Object.keys(resetOptions).length > 0) {
        onResetApprovers(resetOptions);
      } else {
        // If no options selected, just close the modal
        toast.info('No reset options selected');
      }
      setShowResetConfirm(false);
    }
  };

  const totalSelected = selectedApprovers.length + (selectedHodIds ? 1 : 0);
  const approvedApprovers = getApprovedApprovers();
  const hasApprovals = approvedApprovers.length > 0;
  const showResetButton = hasConfiguredApprovers() && onResetApprovers;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center mb-4">
        <Users className="w-5 h-5 text-blue-600 mr-2" />
        <h3 className="text-lg font-semibold text-gray-900">Select Approvers</h3>
      </div>
      
      <p className="text-sm text-gray-600 mb-4">
        Choose which approvers are required for this access request. You can select multiple HODs and skip levels as needed.
      </p>

      {/* Assessment Guidelines - Compact */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-6">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Assessment Guidelines</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div className="flex items-center space-x-1">
            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded font-medium">Low</span>
            <span className="text-gray-600">→ Manager</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded font-medium">Medium</span>
            <span className="text-gray-600">→ HOD</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className="px-2 py-0.5 bg-orange-100 text-orange-800 rounded font-medium">High</span>
            <span className="text-gray-600">→ IT HOD</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded font-medium">Critical</span>
            <span className="text-gray-600">→ Director</span>
          </div>
        </div>
      </div>

      <div className="space-y-6 mb-6">
        {/* Manager, IT HOD, Director Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {approverTypes.map(({ key, label, icon: Icon, color }) => {
            const isSelected = selectedApprovers.includes(key);
            const approver = availableApprovers[key];
            
            return (
              <div
                key={key}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  isSelected 
                    ? `border-${color}-500 bg-${color}-50` 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handleApproverToggle(key)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <Icon className={`w-5 h-5 mr-3 flex-shrink-0 ${
                      isSelected ? `text-${color}-600` : 'text-gray-400'
                    }`} />
                    <div className="flex-1">
                      <div className={`font-medium ${
                        isSelected ? `text-${color}-900` : 'text-gray-900'
                      }`}>
                        {label}
                      </div>
                      {approver && (
                        <>
                          <div className={`text-sm ${
                            isSelected ? `text-${color}-700` : 'text-gray-500'
                          }`}>
                            {approver.user_full_name}
                          </div>
                          {approver.delegate && (
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              <div className="flex items-center space-x-1 text-xs">
                                <UserCog className="w-3 h-3 text-blue-600" />
                                <span className="font-medium text-blue-900">Delegated to:</span>
                              </div>
                              <div className="text-xs text-gray-700 mt-0.5">
                                {approver.delegate.user_full_name}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-2 ${
                    isSelected 
                      ? `border-${color}-500 bg-${color}-500` 
                      : 'border-gray-300'
                  }`}>
                    {isSelected && <Check className="w-4 h-4 text-white" />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* All HODs Section - Single Selection (Radio Button Behavior) */}
        {allHods && allHods.length > 0 && (
          <div>
            <div className="flex items-center mb-3">
              <Shield className="w-5 h-5 text-green-600 mr-2" />
              <h4 className="font-semibold text-gray-900">Department HODs</h4>
              <span className="ml-2 text-sm text-gray-500">
                (Select ONE - {selectedHodIds ? '1 selected' : 'none selected'})
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {allHods.map((hod) => {
                const isSelected = selectedHodIds === hod.user_id;
                return (
                  <div
                    key={hod.user_id}
                    className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleHodToggle(hod.user_id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start flex-1">
                        <Shield className={`w-4 h-4 mr-2 flex-shrink-0 mt-0.5 ${
                          isSelected ? 'text-green-600' : 'text-gray-400'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <div className={`font-medium text-sm truncate ${
                            isSelected ? 'text-green-900' : 'text-gray-900'
                          }`}>
                            {hod.user_full_name}
                          </div>
                          {hod.department_name && (
                            <div className={`text-xs truncate ${
                              isSelected ? 'text-green-700' : 'text-gray-500'
                            }`}>
                              {hod.department_name}
                            </div>
                          )}
                          {hod.delegate && (
                            <div className="mt-1 pt-1 border-t border-gray-200">
                              <div className="flex items-center space-x-1 text-xs">
                                <UserCog className="w-3 h-3 text-blue-600 flex-shrink-0" />
                                <span className="font-medium text-blue-900 truncate">
                                  Delegate: {hod.delegate.user_full_name}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-2 ${
                        isSelected 
                          ? 'border-green-500 bg-green-500' 
                          : 'border-gray-300'
                      }`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Show Already Approved Section if there are approvals */}
      {hasApprovals && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-medium text-green-900 mb-2 flex items-center">
                <Check className="w-4 h-4 mr-2" />
                Already Approved ({approvedApprovers.length}):
              </h4>
              <div className="flex flex-wrap gap-2">
                {approvedApprovers.map((approver, idx) => (
                  <span key={idx} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium flex items-center">
                    <Check className="w-3 h-3 mr-1" />
                    {approver.label}
                  </span>
                ))}
              </div>
              <p className="text-sm text-green-700 mt-2">
                These approvals will be retained when you modify the workflow.
              </p>
            </div>
            {onResetApprovers && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowResetConfirm(true);
                }}
                className="ml-4 px-3 py-1.5 text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700 flex items-center whitespace-nowrap"
                title="Reset the approval workflow configuration"
              >
                <X className="w-4 h-4 mr-1" />
                Reset Workflow
              </button>
            )}
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <X className="w-5 h-5 mr-2 text-orange-600" />
              Reset Approval Workflow?
            </h3>
            <p className="text-gray-700 mb-4">
              Select which approval fields to reset:
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3">
              <p className="text-sm text-blue-800 mb-2">
                <strong>Use this when:</strong>
              </p>
              <ul className="text-sm text-blue-700 list-disc ml-5 space-y-1">
                <li>A selected approver is unavailable (on leave, resigned, etc.)</li>
                <li>You need to select a different HOD or approver</li>
                <li>The approval workflow needs to be completely reconfigured</li>
              </ul>
            </div>
            
            {/* Reset Options Checkboxes */}
            <div className="mb-4 space-y-2">
              <label className="flex items-start">
                <input 
                  type="checkbox" 
                  id="reset_approvers_main"
                  className="mr-3 mt-1 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-700">Reset Approver Configuration</span>
                  <div className="text-xs text-gray-500 mt-1">Clear selected approvers but keep existing approvals</div>
                </div>
              </label>
              <label className="flex items-center">
                <input 
                  type="checkbox" 
                  id="reset_manager_approval"
                  defaultChecked={ticket?.ticket_manager === 1}
                  className="mr-3 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700">
                  Reset Manager Approval {ticket?.ticket_manager === 1 && <span className="text-orange-600 font-medium">(Already Approved)</span>}
                </span>
              </label>
              <label className="flex items-center">
                <input 
                  type="checkbox" 
                  id="reset_hod_approval"
                  defaultChecked={ticket?.ticket_hod === 1}
                  className="mr-3 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700">
                  Reset HOD Approval {ticket?.ticket_hod === 1 && <span className="text-orange-600 font-medium">(Already Approved)</span>}
                </span>
              </label>
              <label className="flex items-center">
                <input 
                  type="checkbox" 
                  id="reset_it_hod_approval"
                  defaultChecked={ticket?.ticket_it_hod === 1}
                  className="mr-3 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700">
                  Reset IT HOD Approval {ticket?.ticket_it_hod === 1 && <span className="text-orange-600 font-medium">(Already Approved)</span>}
                </span>
              </label>
              <label className="flex items-center">
                <input 
                  type="checkbox" 
                  id="reset_director_approval"
                  defaultChecked={ticket?.ticket_director === 1}
                  className="mr-3 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700">
                  Reset Director Approval {ticket?.ticket_director === 1 && <span className="text-orange-600 font-medium">(Already Approved)</span>}
                </span>
              </label>
            </div>

            {/* Only show warning if there are completed approvals */}
            {hasApprovals && (
              <div className="bg-orange-50 border border-orange-200 rounded p-3 mb-4">
                <p className="text-sm text-orange-800">
                  <strong>Note:</strong> {approvedApprovers.length} completed approval(s) will be cleared if selected.
                </p>
              </div>
            )}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleResetApprovers}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
              >
                Yes, Reset Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Selected Approvers Summary - Shows in selection order */}
      {totalSelected > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-blue-900 mb-2">New/Modified Approvers ({totalSelected}):</h4>
          <div className="flex flex-wrap gap-2">
            {selectionOrder.map((item, index) => {
              if (item.type === 'approver') {
                const approverType = approverTypes.find(t => t.key === item.id);
                return (
                  <span key={`${item.type}-${item.id}-${index}`} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                    {approverType?.label}
                  </span>
                );
              } else if (item.type === 'hod') {
                const hod = allHods.find(h => h.user_id === item.id);
                return hod && (
                  <span key={`${item.type}-${item.id}-${index}`} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                    HOD: {hod.user_full_name}
                  </span>
                );
              }
              return null;
            })}
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        {/* Reset Workflow - Left side (shows when approvers are configured) */}
        {showResetButton && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowResetConfirm(true);
            }}
            className="px-4 py-2 bg-orange-100 text-orange-700 border border-orange-300 rounded-md hover:bg-orange-200 flex items-center"
            title="Reset the approval workflow configuration"
          >
            <X className="w-4 h-4 mr-2" />
            Reset Workflow
          </button>
        )}
        {!showResetButton && <div></div>}
        
        {/* Action buttons - Right side */}
        <div className="flex space-x-3">
          <button
            onClick={() => {
              setSelectedApprovers([]);
              setSelectedHodIds(null);
              setSelectionOrder([]);
            }}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
          >
            Clear Selection
          </button>
          <button
            onClick={handleSave}
            disabled={totalSelected === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Set Approvers ({totalSelected})
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApproverSelection;
